import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import api from '../../../api/axios';
import useGetUIErrorMessage from '../../../hooks/useGetUIErrorMessage';
import { IApiResponseError, IApiState } from '../../../types/api';
import { RootState } from '../..';
import { showAlert } from '../appToast/appToastSlice';

interface IUpdateTeamMemberProfileRequest {
  firstName: string;
  lastName: string;
  livenessVerificationId: string;
  phoneNumber: string;
  phoneNumberVerificationId: string;
}

interface IUpdateTeamMemberProfileResponse {
  status?: boolean;
  message?: string;
  data?: {
    id?: string;
    firstName?: string;
    middleName?: string | null;
    lastName?: string;
    email?: string;
    phoneNumber?: string;
    gender?: 'male' | 'female';
    dateOfBirth?: string;
    aggregatorCode?: string | null;
    requiresLivenessVerification?: boolean;
    livenessVerificationId: string;
    livenessVerificationStatus: string;
    lastLoginAt: string;
    isBlocked: boolean;
    createdAt: string;
    updatedAt: string;
    onboardingMode: 'online' | 'offline';
    isPasswordChanged: boolean;
    teamMemberProfileStatus: string | null;
    blockedAt: string | null;
    hasPin: boolean;
  };
  errors?: IApiResponseError[];
}

const initialState: IApiState<IUpdateTeamMemberProfileResponse> = {
  value: null,
  status: 'idle',
  error: '',
};

export const updateTeamMemberProfileAsync = createAsyncThunk(
  'teamMember/profileUpdate',
  async (
    values: IUpdateTeamMemberProfileRequest,
    { rejectWithValue, dispatch, getState }
  ) => {
    try {
      const state = getState() as RootState;

      const accessToken = state.auth.value?.data?.token?.accessToken;

      const businessId =
        localStorage.getItem('businessId') ||
        state.auth.value?.data?.user?.businesses?.[0]?.id;

      const config = {
        headers: {
          Authorization: `Token ${accessToken}`,
          'X-Business-Id': businessId,
        },
      };

      const { data } = await api.put<IUpdateTeamMemberProfileResponse>(
        `/team-members/update-profile`,
        values,
        config
      );

      dispatch(
        showAlert({
          status: 'success',
          description:
            data?.message ?? 'Team member profile updated successfully',
        })
      );

      dispatch(resetTeamMemberProfileUpdate());

      return data;
    } catch (err) {
      const error = err as AxiosError<IUpdateTeamMemberProfileResponse>;

      const errorMessage = useGetUIErrorMessage({
        message: error.response?.data?.message,
        errors: error.response?.data?.errors,
      });

      dispatch(
        showAlert({
          status: 'success',
          description: errorMessage,
        })
      );

      return rejectWithValue(error.response?.data);
    }
  }
);

export const teamMemberProfileUpdateSlice = createSlice({
  name: 'teamMember/update',
  initialState,
  reducers: {
    resetTeamMemberProfileUpdate(state) {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(updateTeamMemberProfileAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(updateTeamMemberProfileAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload as IUpdateTeamMemberProfileResponse;
      })
      .addCase(updateTeamMemberProfileAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.value = action.payload as IUpdateTeamMemberProfileResponse;
        state.error = action.error.message;
      });
  },
});

export const { resetTeamMemberProfileUpdate } =
  teamMemberProfileUpdateSlice.actions;

export default teamMemberProfileUpdateSlice.reducer;
